import React, { useEffect, useRef } from 'react';
import { canRequestGoogleAds } from '../../utils/googleCmp';
import { config } from '../../config';
import { useAds } from '../../contexts/AdsContext';
import styles from './AdBanner.module.css';

const SIZE_PRESETS = {
  rectangle: {
    width: 300,
    height: 250,
    minViewportWidth: 0,
    layout: 'visualCard',
    mockContents: [
      {
        brand: 'LinguaSprint',
        title: "Impara l'Italiano Velocemente",
        displayUrl: 'www.linguasprint.it/corsi',
        description: 'Corsi intensivi online con insegnanti madrelingua e percorsi pratici per parlare con piu sicurezza.',
        cta: 'Scopri di piu',
        sponsor: 'Sponsored',
        imageLabel: 'Live lesson preview',
      },
      {
        brand: 'Ascolta Facile',
        title: 'Podcast Facili in Italiano',
        displayUrl: 'www.ascoltafacile.it/podcast',
        description: 'Episodi brevi con trascrizione, glossari e quiz per migliorare comprensione e pronuncia ogni giorno.',
        cta: 'Ascolta Ora',
        sponsor: 'Sponsored',
        imageLabel: 'Audio lesson graphic',
      },
      {
        brand: 'Grammatica Pratica',
        title: 'Ripasso Verbi Italiani',
        displayUrl: 'www.grammaticapratica.it/verbi',
        description: 'Schede smart, esercizi corretti in tempo reale e percorsi guidati su tempi, modi e usi frequenti.',
        cta: 'Prova Gratis',
        sponsor: 'Sponsored',
        imageLabel: 'Verb drills mockup',
      },
    ],
  },
  largeRectangle: {
    width: 336,
    height: 280,
    minViewportWidth: 0,
    layout: 'visualCard',
    mockContents: [
      {
        brand: 'Studio Italiano',
        title: 'Parla Meglio in 30 Giorni',
        displayUrl: 'www.studioitaliano.it/fluency',
        description: 'Video lezioni, correzioni personalizzate e sessioni dal vivo per sbloccare conversazione e ascolto.',
        cta: 'Prenota Ora',
        sponsor: 'Sponsored',
        imageLabel: 'Course dashboard',
      },
      {
        brand: 'Lessico Attivo',
        title: 'Schede Lessicali per Studenti',
        displayUrl: 'www.lessicoattivo.it/cards',
        description: 'Set quotidiani di vocaboli, esempi reali e mini sfide per fissare davvero le parole nuove.',
        cta: 'Inizia Gratis',
        sponsor: 'Sponsored',
        imageLabel: 'Vocabulary flashcards',
      },
      {
        brand: 'Dialoghi Veri',
        title: 'Allenati con Dialoghi Reali',
        displayUrl: 'www.dialoghiveri.it/club',
        description: 'Ascolta, ripeti e comprendi situazioni quotidiane con spiegazioni chiare e ritmo graduale.',
        cta: 'Guarda Demo',
        sponsor: 'Sponsored',
        imageLabel: 'Conversation lesson',
      },
    ],
  },
  mobileBanner: {
    width: 320,
    height: 50,
    minViewportWidth: 0,
    layout: 'compactShort',
    mockContents: [
      {
        title: 'Italiano in 5 minuti',
        displayUrl: 'italianoflash.it',
        description: 'Mini lezioni e quiz giornalieri.',
        cta: 'Apri',
      },
      {
        title: 'Podcast facili quotidiani',
        displayUrl: 'ascoltofacile.it',
        description: 'Dialoghi brevi con trascrizione.',
        cta: 'Ascolta',
      },
      {
        title: "Verbi pronti all'uso",
        displayUrl: 'verbiattivi.it',
        description: 'Ripasso pratico e schede rapide.',
        cta: 'Prova',
      },
    ],
  },
  largeMobileBanner: {
    width: 320,
    height: 100,
    minViewportWidth: 0,
    layout: 'compactHorizontal',
    mockContents: [
      {
        brand: 'Italiano Live',
        title: '15 minuti al giorno. Parla meglio.',
        displayUrl: 'italianolive.it/sessioni',
        description: 'Micro-lezioni con tutor e quiz rapidi.',
        cta: 'Scopri',
        badge: 'Ad',
      },
      {
        brand: 'Vocabolario Plus',
        title: '20 parole nuove ogni settimana.',
        displayUrl: 'vocabolarioplus.it',
        description: 'Schede guidate e ripasso smart da mobile.',
        cta: 'Apri',
        badge: 'Ad',
      },
      {
        brand: 'Ascolto Attivo',
        title: 'Dialoghi autentici, piu comprensione.',
        displayUrl: 'ascoltoattivo.it/app',
        description: 'Clip brevi con trascrizione e guida rapida.',
        cta: 'Ascolta',
        badge: 'Ad',
      },
    ],
  },
  leaderboard: {
    width: 728,
    height: 90,
    minViewportWidth: 768,
    layout: 'horizontal',
    mockContents: [
      {
        brand: 'Italiano Studio',
        title: 'Corsi di Italiano dal Vivo',
        displayUrl: 'www.italianostudio.it/live',
        description: 'Lezioni in piccoli gruppi con insegnanti madrelingua e materiali inclusi.',
        cta: 'Prenota Ora',
        badge: 'Ad',
      },
      {
        brand: 'Italiano Facile',
        title: 'Lezioni Online per Parlare con Sicurezza',
        displayUrl: 'www.italianofacile.it/live',
        description: 'Mini classi, feedback reale e percorsi su misura per ogni livello.',
        cta: 'Prenota',
        badge: 'Ad',
      },
      {
        brand: 'Lessico Attivo',
        title: 'Parole Crociate per Studenti',
        displayUrl: 'www.lessicoattivo.it/crossword',
        description: 'Schede lessicali, cruciverba graduati e sfide linguistiche da 5 minuti.',
        cta: 'Inizia Gratis',
        badge: 'Ad',
      },
    ],
  },
  largeLeaderboard: {
    width: 970,
    height: 90,
    minViewportWidth: 1024,
    layout: 'horizontal',
    mockContents: [
      {
        brand: 'Parole Vive',
        title: 'Migliora il tuo italiano scritto senza perdere tempo.',
        displayUrl: 'www.parolevive.it/masterclass',
        description: 'Laboratori online con feedback personalizzato e percorsi rapidi per chi studia ogni giorno.',
        cta: 'Guarda Demo',
        badge: 'Ad',
      },
      {
        brand: 'Lingua Sprint',
        title: 'Fai un salto di livello con sessioni brevi ma efficaci.',
        displayUrl: 'www.linguasprint.it/boost',
        description: 'Conversazione, lessico e ascolto con una routine facile da seguire.',
        cta: 'Scopri',
        badge: 'Ad',
      },
      {
        brand: 'Dialoghi Veri',
        title: 'Allenati con dialoghi autentici e spiegazioni chiare.',
        displayUrl: 'www.dialoghiveri.it',
        description: 'Contenuti pratici per capire davvero l’italiano usato ogni giorno.',
        cta: 'Apri',
        badge: 'Ad',
      },
    ],
  },
  halfPage: {
    width: 300,
    height: 600,
    minViewportWidth: 0,
    layout: 'visualCard',
    mockContents: [
      {
        brand: 'Viaggi Cultura',
        title: 'Vacanze Studio in Italia',
        displayUrl: 'www.viaggicultura.it/italia',
        description: 'Immergiti nella lingua e nella cultura italiana con soggiorni nelle piu belle citta d’arte.',
        cta: 'Prenota Subito',
        sponsor: 'Sponsored',
        imageLabel: 'Travel collage',
      },
      {
        brand: 'Parole Vive',
        title: 'Masterclass di Scrittura',
        displayUrl: 'www.parolevive.it/masterclass',
        description: 'Laboratori online per migliorare lessico, stile e naturalezza con feedback ogni settimana.',
        cta: 'Scopri il Corso',
        sponsor: 'Sponsored',
        imageLabel: 'Writing workshop',
      },
      {
        brand: 'Leggiamo Insieme',
        title: 'Club di Lettura in Italiano',
        displayUrl: 'www.leggiamoinsieme.it/club',
        description: 'Romanzi brevi, incontri guidati e materiali di supporto per discutere storie italiane senza stress.',
        cta: 'Unisciti',
        sponsor: 'Sponsored',
        imageLabel: 'Book club visual',
      },
    ],
  },
  skyscraper: {
    width: 160,
    height: 600,
    minViewportWidth: 0,
    layout: 'stackedCompact',
    mockContents: [
      {
        brand: 'Italiano Pro',
        title: 'Corso Intensivo di Italiano',
        displayUrl: 'www.italianopro.it/intensivo',
        description: 'Lezioni brevi ogni giorno, esercizi guidati e tutor madrelingua.',
        cta: 'Inizia Ora',
        sponsor: 'Sponsored',
        imageLabel: 'Lesson mockup',
      },
      {
        brand: 'Verbi Attivi',
        title: 'Verbi Italiani Senza Stress',
        displayUrl: 'www.verbiattivi.it/pratica',
        description: 'Ripasso intelligente e quiz mirati per ricordare forme e usi piu frequenti.',
        cta: 'Prova Gratis',
        sponsor: 'Sponsored',
        imageLabel: 'Verb cards',
      },
      {
        brand: 'Ascolto Facile',
        title: 'Podcast + Trascrizioni',
        displayUrl: 'www.ascoltofacile.it/lezioni',
        description: 'Ascolta dialoghi autentici e migliora comprensione e pronuncia.',
        cta: 'Ascolta',
        sponsor: 'Sponsored',
        imageLabel: 'Audio lesson',
      },
    ],
  },
};

const DEFAULT_SIZE = 'leaderboard';

const hasExistingAdSenseFill = (node) => (
  Boolean(node?.getAttribute?.('data-adsbygoogle-status'))
);

const scheduleBannerInitialization = (callback) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const timeoutId = window.setTimeout(callback, 0);
  return () => window.clearTimeout(timeoutId);
};

const pickRandomMockContent = (mockContents) => {
  if (!Array.isArray(mockContents) || mockContents.length === 0) {
    return null;
  }

  const index = Math.floor(Math.random() * mockContents.length);
  return mockContents[index] || mockContents[0];
};

const useMatchesMinViewportWidth = (minViewportWidth) => {
  const getMatches = () => {
    if (typeof window === 'undefined') return true;
    return window.innerWidth >= minViewportWidth;
  };

  const [matches, setMatches] = React.useState(getMatches);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(`(min-width: ${minViewportWidth}px)`);
    const handleChange = (event) => {
      setMatches(event.matches);
    };

    setMatches(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [minViewportWidth]);

  return matches;
};

const useViewportWidth = () => {
  const getWidth = () => {
    if (typeof window === 'undefined') return 1920;
    return window.innerWidth;
  };

  const [width, setWidth] = React.useState(getWidth);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
};

const MockAdChrome = ({ position = 'top', label = 'Ads by Google' }) => (
  <div
    className={position === 'bottom' ? styles.mockChromeBottom : styles.mockChromeTop}
    aria-hidden="true"
  >
    <div className={styles.mockAdChoices}>
      <span className={styles.mockAdChoicesIcon}>
        <span className={styles.mockAdChoicesInfo}>i</span>
        <span className={styles.mockAdChoicesTriangle} />
      </span>
      <span className={styles.mockAdChoicesLabel}>{label}</span>
    </div>
    <span className={styles.mockCloseButton}>×</span>
  </div>
);

const renderMockPlaceholder = ({ layout, mockContent }) => {
  if (layout === 'compactShort') {
    return (
      <div className={styles.compactShortPlaceholder}>
        <div className={styles.compactShortMain}>
          <div className={styles.compactShortTopRow}>
            <span className={styles.compactShortBadge}>Ad</span>
            <span className={styles.compactShortHeadline}>{mockContent.title}</span>
          </div>
          <span className={styles.compactShortLink}>{mockContent.displayUrl}</span>
        </div>
        <div className={styles.compactShortAside}>
          <span className={styles.compactShortProvider}>Ads by Google</span>
          <span className={styles.compactShortCta}>{mockContent.cta}</span>
        </div>
      </div>
    );
  }

  if (layout === 'short') {
    return (
      <div className={styles.shortPlaceholder}>
        <div className={styles.shortMain}>
          <span className={styles.shortHeadline}>{mockContent.title}</span>
          <span className={styles.shortLink}>{mockContent.displayUrl}</span>
        </div>
        <div className={styles.shortMeta}>
          <span className={styles.shortProvider}>Ads by Google</span>
          <span className={styles.shortBadge}>FAKE AD</span>
          <span className={styles.shortCta}>{mockContent.cta}</span>
          <span className={styles.shortCloseButton}>×</span>
        </div>
      </div>
    );
  }

  if (layout === 'horizontal') {
    return (
      <div className={styles.horizontalPlaceholder}>
        <MockAdChrome />
        <div className={styles.horizontalMeta}>
          <span className={styles.horizontalBadge}>{mockContent.badge || 'Ad'}</span>
          <span className={styles.horizontalLogo}>{(mockContent.brand || 'Brand').slice(0, 2).toUpperCase()}</span>
        </div>
        <div className={styles.horizontalContent}>
          <span className={styles.horizontalHeadline}>{mockContent.title}</span>
          <span className={styles.horizontalBody}>{mockContent.description}</span>
          <span className={styles.horizontalLink}>{mockContent.displayUrl}</span>
        </div>
        <div className={styles.horizontalAside}>
          <span className={styles.horizontalCta}>{mockContent.cta}</span>
          <span className={styles.horizontalClose}>×</span>
        </div>
      </div>
    );
  }

  if (layout === 'compactHorizontal') {
    return (
      <div className={styles.compactHorizontalPlaceholder}>
        <MockAdChrome label="Ads" />
        <div className={styles.compactHorizontalMeta}>
          <span className={styles.compactHorizontalBadge}>{mockContent.badge || 'Ad'}</span>
          <span className={styles.compactHorizontalLogo}>
            {(mockContent.brand || 'Brand').slice(0, 2).toUpperCase()}
          </span>
        </div>
        <div className={styles.compactHorizontalContent}>
          <span className={styles.compactHorizontalHeadline}>{mockContent.title}</span>
          <span className={styles.compactHorizontalBody}>{mockContent.description}</span>
          <span className={styles.compactHorizontalLink}>{mockContent.displayUrl}</span>
        </div>
        <div className={styles.compactHorizontalAside}>
          <span className={styles.compactHorizontalCta}>{mockContent.cta}</span>
        </div>
      </div>
    );
  }

  if (layout === 'stackedCompact') {
    return (
      <div className={styles.stackedPlaceholder}>
        <MockAdChrome />
        <div className={styles.stackedVisual}>
          <span className={styles.stackedVisualLabel}>{mockContent.imageLabel}</span>
        </div>
        <div className={styles.stackedContent}>
          <div className={styles.stackedSponsorRow}>
            <span className={styles.stackedBrand}>{mockContent.brand}</span>
            <span className={styles.stackedSponsor}>{mockContent.sponsor || 'Sponsored'}</span>
          </div>
          <span className={styles.stackedHeadline}>{mockContent.title}</span>
          <span className={styles.stackedBody}>{mockContent.description}</span>
          <span className={styles.stackedLink}>{mockContent.displayUrl}</span>
          <span className={styles.stackedCta}>{mockContent.cta}</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.visualPlaceholder}>
      <MockAdChrome />
      <div className={styles.visualHero}>
        <span className={styles.visualHeroLabel}>{mockContent.imageLabel}</span>
      </div>
      <div className={styles.visualContent}>
        <div className={styles.visualSponsorRow}>
          <span className={styles.visualBrand}>{mockContent.brand}</span>
          <span className={styles.visualSponsor}>{mockContent.sponsor || 'Sponsored'}</span>
        </div>
        <span className={styles.visualHeadline}>{mockContent.title}</span>
        <span className={styles.visualBody}>{mockContent.description}</span>
        <div className={styles.visualFooter}>
          <span className={styles.visualLink}>{mockContent.displayUrl}</span>
          <span className={styles.visualCta}>{mockContent.cta}</span>
        </div>
      </div>
    </div>
  );
};

const AdBanner = ({
  placement = 'display-banner',
  size = DEFAULT_SIZE,
  mobileSize = null,
  mobileBreakpoint = 768,
  responsive = false,
  minViewportWidth = null,
  align = 'center',
  className = '',
  testId = 'ad-banner',
}) => {
  const viewportWidth = useViewportWidth();
  const shouldUseMobilePreset = mobileSize && viewportWidth < mobileBreakpoint;
  const resolvedSize = shouldUseMobilePreset ? mobileSize : size;
  const preset = SIZE_PRESETS[resolvedSize] || SIZE_PRESETS[DEFAULT_SIZE];
  const width = preset.width;
  const height = preset.height;
  const resolvedMinViewportWidth = Number.isInteger(minViewportWidth)
    ? minViewportWidth
    : preset.minViewportWidth;
  const { showAds, shouldShowLiveAds, shouldShowFakeBanner, areAdsBlocked } = useAds();
  const bannerRef = useRef(null);
  const initializedNodesRef = useRef(new WeakSet());
  const mockContentRef = useRef(null);
  const mockContentKeyRef = useRef(null);
  const isVisibleViewport = useMatchesMinViewportWidth(resolvedMinViewportWidth);

  const mockContentKey = `${placement}:${resolvedSize}`;
  if (mockContentKeyRef.current !== mockContentKey) {
    mockContentRef.current = pickRandomMockContent(preset.mockContents);
    mockContentKeyRef.current = mockContentKey;
  }

  useEffect(() => {
    if (!shouldShowLiveAds || !isVisibleViewport) return;
    if (!config.ADSENSE_CLIENT || !config.ADSENSE_DISPLAY_SLOT) return;
    const node = bannerRef.current;
    if (!node || !node.isConnected) return;
    if (initializedNodesRef.current.has(node)) return;

    if (hasExistingAdSenseFill(node)) {
      initializedNodesRef.current.add(node);
      return;
    }

    let isCancelled = false;
    const cancelScheduledInitialization = scheduleBannerInitialization(() => {
      if (!canRequestGoogleAds()) return;
      if (isCancelled || !node.isConnected) return;
      if (initializedNodesRef.current.has(node)) return;

      if (hasExistingAdSenseFill(node)) {
        initializedNodesRef.current.add(node);
        return;
      }

      try {
        initializedNodesRef.current.add(node);
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({});
      } catch (error) {
        initializedNodesRef.current.delete(node);
        console.warn(`[Ads] Failed to render banner for ${placement}.`, error);
      }
    });

    return () => {
      isCancelled = true;
      cancelScheduledInitialization();
    };
  }, [isVisibleViewport, placement, shouldShowLiveAds]);

  if (!showAds || areAdsBlocked || !isVisibleViewport) {
    return null;
  }

  const sizeStyle = {
    '--ad-banner-width-px': `${width}px`,
    '--ad-banner-height-px': `${height}px`,
    '--ad-banner-aspect-ratio': `${width} / ${height}`,
  };
  const wrapperClassName = [styles.wrapper, className].filter(Boolean).join(' ');
  const frameClassName = [
    styles.frame,
    responsive ? styles.responsiveFrame : styles.fixedFrame,
    align === 'start' ? styles.alignStart : styles.alignCenter,
  ].join(' ');
  const mockContent = mockContentRef.current || pickRandomMockContent(preset.mockContents);
  const isCompactMobileMock = ['compactShort', 'compactHorizontal'].includes(preset.layout);

  if (shouldShowFakeBanner) {
    return (
      <div
        className={wrapperClassName}
        style={sizeStyle}
        data-testid={`${testId}-fake`}
        data-ad-placement={placement}
        data-ad-size={resolvedSize}
      >
        <div className={`${frameClassName} ${styles.mockFrame}`}>
          <div className={`${styles.fakeRibbon} ${isCompactMobileMock ? styles.compactFakeRibbon : ''}`}>
            Fake
          </div>
          {renderMockPlaceholder({ layout: preset.layout, mockContent })}
        </div>
      </div>
    );
  }

  if (!shouldShowLiveAds || !config.ADSENSE_CLIENT || !config.ADSENSE_DISPLAY_SLOT) {
    return null;
  }

  return (
    <div
      className={wrapperClassName}
      style={sizeStyle}
      data-testid={`${testId}-live`}
      data-ad-placement={placement}
      data-ad-size={resolvedSize}
    >
      <div className={`${frameClassName} ${styles.liveFrame}`}>
        <ins
          ref={bannerRef}
          className={`adsbygoogle ${styles.ins}`}
          data-ad-client={config.ADSENSE_CLIENT}
          data-ad-slot={config.ADSENSE_DISPLAY_SLOT}
          data-full-width-responsive="false"
        />
      </div>
    </div>
  );
};

export default AdBanner;
